/**
 * QR lot labels — print physical labels that tie shelves/bins/toploaders back
 * to lots. Each label = QR code (deep link: <app>#lot=<id>, so ANY phone
 * camera opens the lot drawer) + the lot ID + optional name/detail line.
 *
 * Built for LABEL WRITERS (DYMO / Brother roll printers): every label is its
 * own page via @page { size } + page-break-after, so printing N labels feeds
 * N labels through the roll. Also fine on a normal printer one-per-sheet.
 *
 * Physical-world guidance baked into the modal copy: label sleeves, bins, and
 * toploaders — never the collectible itself (stickers on sealed hurt resale).
 */
import QRCode from "qrcode";
import { DB } from "../lib/db";
import { escapeHtml, toast } from "../lib/dom";
import { openModal, closeModal } from "./modal";

interface LabelPreset { key: string; name: string; w: number; h: number }
const PRESETS: LabelPreset[] = [
  { key: "dymo30252",    name: "DYMO 30252 Address (89 × 28 mm)",  w: 89, h: 28 },
  { key: "dymo30334",    name: "DYMO 30334 Medium (57 × 32 mm)",   w: 57, h: 32 },
  { key: "brother29x90", name: "Brother DK-1201 (90 × 29 mm)",     w: 90, h: 29 },
  { key: "square25",     name: "Small square (25 × 25 mm)",        w: 25, h: 25 },
  { key: "custom",       name: "Custom size…",                     w: 60, h: 30 },
];
const PREF_KEY = "enigma_label_prefs_v1";

interface LabelPrefs { preset: string; w: number; h: number; includeName: boolean }
function loadPrefs(): LabelPrefs {
  try {
    const p = JSON.parse(localStorage.getItem(PREF_KEY) || "{}");
    return { preset: p.preset || "dymo30252", w: Number(p.w) || 89, h: Number(p.h) || 28, includeName: p.includeName !== false };
  } catch { return { preset: "dymo30252", w: 89, h: 28, includeName: true }; }
}
function savePrefs(p: LabelPrefs): void {
  localStorage.setItem(PREF_KEY, JSON.stringify(p));
}

let _ctx: { kind: "card" | "sealed"; lotIds: string[] } | null = null;

export function openLabelPrint(kind: "card" | "sealed", lotIds: string[]): void {
  if (!lotIds.length) { toast("Select at least one lot first.", "err"); return; }
  _ctx = { kind, lotIds };
  const prefs = loadPrefs();
  const body = `
    <div class="space-y-3">
      <div class="text-sm">Printing <b>${lotIds.length}</b> label${lotIds.length === 1 ? "" : "s"} — one per label, QR opens the lot on any phone camera.</div>
      <div class="grid grid-cols-12 gap-2">
        <div class="col-span-12 sm:col-span-6">
          <label class="label text-xs">Label size</label>
          <select class="select" id="lblPreset" onchange="_lblPresetChange()">
            ${PRESETS.map(p => `<option value="${p.key}" ${prefs.preset === p.key ? "selected" : ""}>${escapeHtml(p.name)}</option>`).join("")}
          </select>
        </div>
        <div class="col-span-6 sm:col-span-3 ${prefs.preset === "custom" ? "" : "hidden"}" data-lbl-custom>
          <label class="label text-xs">Width (mm)</label>
          <input class="input" id="lblW" type="number" step="1" min="15" value="${prefs.w}">
        </div>
        <div class="col-span-6 sm:col-span-3 ${prefs.preset === "custom" ? "" : "hidden"}" data-lbl-custom>
          <label class="label text-xs">Height (mm)</label>
          <input class="input" id="lblH" type="number" step="1" min="15" value="${prefs.h}">
        </div>
        <div class="col-span-12">
          <label class="inline-flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" id="lblName" ${prefs.includeName ? "checked" : ""}>
            <span>Include name / detail line (skipped automatically on square labels)</span>
          </label>
        </div>
      </div>
      <div class="panel-2 p-2 text-xs text-[var(--muted)]">
        In the print dialog: pick your label printer, match the paper size to the label, margins none, scale 100%.
        Stick labels on <b>toploaders, sleeves, bins, and shelves</b> — never on the card or sealed product itself.
      </div>
    </div>`;
  const footer = `
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" onclick="_lblPrint()">\u{1F5A8} Print labels</button>`;
  openModal({ title: "Print QR lot labels", body, footer });
}

function lblPresetChange(): void {
  const key = (document.getElementById("lblPreset") as HTMLSelectElement | null)?.value || "dymo30252";
  const isCustom = key === "custom";
  document.querySelectorAll<HTMLElement>("[data-lbl-custom]").forEach(el => el.classList.toggle("hidden", !isCustom));
  if (!isCustom) {
    const p = PRESETS.find(x => x.key === key)!;
    (document.getElementById("lblW") as HTMLInputElement | null)!.value = String(p.w);
    (document.getElementById("lblH") as HTMLInputElement | null)!.value = String(p.h);
  }
}

async function lblPrint(): Promise<void> {
  if (!_ctx) return;
  const preset = (document.getElementById("lblPreset") as HTMLSelectElement | null)?.value || "dymo30252";
  const w = Math.max(15, Number((document.getElementById("lblW") as HTMLInputElement | null)?.value) || 89);
  const h = Math.max(15, Number((document.getElementById("lblH") as HTMLInputElement | null)?.value) || 28);
  const includeName = !!(document.getElementById("lblName") as HTMLInputElement | null)?.checked;
  savePrefs({ preset, w, h, includeName });

  const table = _ctx.kind === "card" ? "cards" : "sealed";
  const keyField = _ctx.kind === "card" ? "Card Lot ID" : "Lot ID";
  const base = window.location.origin + window.location.pathname;
  const vertical = w / h < 1.6; // square-ish labels stack QR over text

  const labels: string[] = [];
  for (const id of _ctx.lotIds) {
    const row = DB.table(table).find(x => x[keyField] === id);
    if (!row) continue;
    const name = _ctx.kind === "card" ? String(row["Card Name"] || "") : String(row["Product"] || "");
    const detail = _ctx.kind === "card"
      ? [row["Set"], row["Card #"] ? "#" + row["Card #"] : "", row["Condition/Grade"]].filter(Boolean).join(" · ")
      : [row["Set"], row["Product Type"]].filter(Boolean).join(" · ");
    let qr = "";
    try {
      qr = await QRCode.toDataURL(`${base}#lot=${encodeURIComponent(id)}`, {
        margin: 0, width: 300, errorCorrectionLevel: "M",
      });
    } catch (e) {
      console.error("[labels] QR failed for", id, e);
      continue;
    }
    labels.push(`
      <div class="label ${vertical ? "v" : ""}">
        <img src="${qr}" alt="">
        <div class="meta">
          <div class="id">${escapeHtml(id)}</div>
          ${includeName && !vertical ? `<div class="nm">${escapeHtml(name)}</div><div class="nm sub">${escapeHtml(detail)}</div>` : ""}
        </div>
      </div>`);
  }
  if (!labels.length) { toast("No printable lots found", "err"); return; }

  const html = `<!DOCTYPE html><html><head><title>Lot labels</title><style>
    @page { size: ${w}mm ${h}mm; margin: 0; }
    html, body { margin: 0; padding: 0; }
    .label { width: ${w}mm; height: ${h}mm; box-sizing: border-box; padding: 1.5mm;
      display: flex; align-items: center; gap: 2mm; overflow: hidden;
      page-break-after: always; font-family: Arial, Helvetica, sans-serif; }
    .label img { height: ${Math.max(10, h - 4)}mm; width: ${Math.max(10, h - 4)}mm; flex: 0 0 auto; }
    .meta { min-width: 0; }
    .id { font-family: 'Courier New', monospace; font-weight: bold; font-size: ${h >= 30 ? 11 : 9.5}pt; }
    .nm { font-size: 7.5pt; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: ${w - h - 4}mm; }
    .nm.sub { color: #444; font-size: 6.5pt; }
    .label.v { flex-direction: column; justify-content: center; gap: 1mm; padding: 1mm; }
    .label.v img { height: ${Math.max(10, h - 9)}mm; width: ${Math.max(10, h - 9)}mm; }
    .label.v .id { font-size: 6.5pt; text-align: center; }
  </style></head><body>${labels.join("")}
  <script>window.onload = function () { setTimeout(function () { window.print(); }, 200); };<\/script>
  </body></html>`;

  const win = window.open("", "_blank", "width=640,height=640");
  if (!win) { toast("Popup blocked — allow popups to print labels", "err"); return; }
  win.document.write(html);
  win.document.close();
  win.focus();
  closeModal();
  toast(`${labels.length} label${labels.length === 1 ? "" : "s"} sent to the print window`);
}

// Inline-handler bridges
(window as any).openLabelPrint   = openLabelPrint;
(window as any)._lblPresetChange = lblPresetChange;
(window as any)._lblPrint        = () => { void lblPrint(); };
